import { v4 as uuid } from "uuid";

import { authAPIUrl } from "@/environment";
import { BackendError, CancelError } from "@/error";
import { identityChanged } from "@/sessionIdentity";

import { makeApiClient } from "./apiClient.axios";

let refreshing = null;

export function createApiClient(baseUrl, optionsOrBasePath, options) {
  if (typeof optionsOrBasePath === "string") {
    baseUrl += "/" + optionsOrBasePath;
  } else {
    options = optionsOrBasePath;
  }
  const client = makeApiClient(baseUrl, options);
  return makeMethods(async (config) => {
    try {
      return unwrap(await withAuthRetry(() => client.request(withCorrelationId(config))));
    } catch (error) {
      throw asBackendError(error);
    }
  });
}

export function createOldApiClient(baseUrl, options) {
  const client = makeApiClient(baseUrl, options);
  return makeMethods((config) => withAuthRetry(() => client.request(withCorrelationId(config))));
}

export function createRawApiClient(baseUrl, options) {
  const client = makeApiClient(baseUrl, options);
  return makeMethods((config) => client.request(withCorrelationId(config)));
}

// dedicated client for token refresh — avoids re-entering withAuthRetry
const rawClient = createRawApiClient(authAPIUrl);

function makeMethods(send) {
  return {
    get(url, config) {
      return send({ ...config, method: "get", url: norm(url, arguments.length) });
    },
    post(url, data, config) {
      return send({ ...config, method: "post", url: norm(url, arguments.length), data });
    },
    put(url, data, config) {
      return send({ ...config, method: "put", url: norm(url, arguments.length), data });
    },
    patch(url, data, config) {
      return send({ ...config, method: "patch", url: norm(url, arguments.length), data });
    },
    delete(url, config) {
      return send({ ...config, method: "delete", url: norm(url, arguments.length) });
    },
  };

  function norm(url, argCount) {
    if (argCount === 0) {
      return "";
    }
    if (url === null || url === undefined) {
      throw new TypeError("url must not be null or undefined");
    }
    return String(url);
  }
}

function withCorrelationId(config) {
  return {
    ...config,
    headers: { ...config?.headers, "correlation-id": uuid() },
  };
}

function unwrap(response) {
  const data = response.data;
  // if responseType is set (for binary response data), just return raw data
  if (response.config.responseType) {
    return data;
  }
  if (data.success === false || ("status" in data && data.status !== "success")) {
    throw BackendError.fromHttpResponse(response);
  }
  return data.data;
}

function asBackendError(error) {
  if (error.response) {
    return BackendError.fromHttpResponse(error.response);
  }
  if (error.config) {
    return BackendError.fromHttpError(error);
  }
  return error;
}

async function withAuthRetry(request, retried = false) {
  try {
    return await request();
  } catch (error) {
    if (error.response?.status !== 401 || retried) {
      throw error;
    }
    if (error.response.data?.error?.code !== "TOKEN_EXPIRED") {
      return redirectToLogin();
    }
    try {
      await refreshAuthToken();
    } catch {
      return redirectToLogin();
    }
    return withAuthRetry(request, true);
  }
}

function redirectToLogin() {
  window.location.assign(authAPIUrl + "/auth/login?type=admin");
  throw new CancelError("Unauthorized");
}

// Refresh tokens are one-time-use: the 401 retry above and the WAMP
// expired_access_token path (server/index.js) must share one in-flight request.
export function refreshAuthToken() {
  refreshing ??= requestTokenRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function requestTokenRefresh() {
  const res = await rawClient.get("auth/token/refresh?type=admin");
  // the refreshed cookie may run another NATS scheme or role (flag flip); a live
  // connection keeps the old subjects, so the page must restart. Before NATS is
  // up nothing is stale yet: openConnection reads the scheme fresh.
  // Lazy import: the store pulls in handlers that import this module.
  const { useAppStore } = await import("@/stores");
  const store = useAppStore();
  if (store.natsconnection && identityChanged(store, res.data)) window.location.reload();
  return res.data;
}
